import * as React from 'react'
import { useForm, useFieldArray } from 'react-hook-form'

import { SupportMemo as Presenter, IProps as IPresenter } from './presenter'
import { parseDate, parseDateString } from '@/utils/dateFormat'
import { useInitialize } from '@/hooks/useInitialize'
import { initSupportEvents, buildSupportEvent } from '@/utils/reserve'
import {
  tmpSaveSittingReportStep2,
  buildTmpSaveEventParams,
  buildReserveOption,
} from '@/apis/reserves'

interface IProps
  extends Omit<
    IPresenter,
    | 'inputSupportEvents'
    | 'minDate'
    | 'showConfirm'
    | 'showDeleteConfirm'
    | 'register'
    | 'setValue'
    | 'addSupportEventHandler'
    | 'openSupportEventHandler'
    | 'closeSupportEventHandler'
    | 'deleteSupportEventHandler'
    | 'confirmApproveHandler'
    | 'confirmCancelHandler'
    | 'deleteApproveHandler'
    | 'deleteCancelHandler'
  > {
  reserve: IReserveSummary
  requestedChildIds: number[]
  options: IOption[]
  defaultSupportEvents: IEventApi[]
  startAt: string
  returnUrl: string
}

export const SupportMemo = ({
  reserve,
  requestedChildIds,
  options,
  defaultSupportEvents,
  startAt,
  returnUrl,
  ...props
}: IProps) => {
  const [isInitialized] = useInitialize()
  const [showToast, setShowToast] = React.useState<boolean>(false)
  const [showError, setShowError] = React.useState<boolean>(false)
  const [errorMessages, setErrorMessages] = React.useState<string[]>([])
  const [showConfirm, setShowConfirm] = React.useState<boolean>(false)
  const [showDeleteConfirm, setShowDeleteConfirm] =
    React.useState<boolean>(false)
  const [deleteIndex, setDeleteIndex] = React.useState<number>()
  const [showSupportEventModal, setSupportEventModal] =
    React.useState<boolean>(false)
  const [isEdited, setIsEdited] = React.useState<boolean>(false)
  const { hour, minute } = parseDateString(startAt)
  const startDate = parseDate(startAt)
  const initOptions = options.map((option) => buildReserveOption(option))

  /*
   * Form設定
   */

  const {
    control,
    watch,
    register,
    setValue,
    formState: { errors },
  } = useForm<IReserveSupportMemo>({
    mode: 'onChange',
    defaultValues: {
      supportEvents: initSupportEvents(
        defaultSupportEvents,
        startDate,
        hour,
        minute,
        requestedChildIds,
      ),
      options: initOptions,
    },
  })
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'supportEvents',
  })

  const supportEvents = watch('supportEvents')
  const inputOptions = watch('options')

  /*
   * 送信
   */

  const submitHandler = async () => {
    const events = buildTmpSaveEventParams(supportEvents, startDate)

    const result = await tmpSaveSittingReportStep2(
      reserve.id,
      reserve.slug,
      inputOptions,
      events,
      false,
      reserve.sitting_report_children_mood,
      reserve.message_on_report_input_long,
      reserve.tokyo_bs_tmp_use_report,
    )

    if (result.result && result.res.result) {
      setIsEdited(false)
      setTimeout(() => {
        setShowToast(false)
      }, 2000),
        setShowToast(true)
    } else {
      showErrorToast(result.res.errors)
    }
  }

  /*
   * トースト表示
   */
  const showErrorToast = (messages: string[]) => {
    setErrorMessages(messages)
    setTimeout(() => {
      setShowError(false)
      setErrorMessages([])
    }, 2000),
      setShowError(true)
  }

  /*
   * 前の画面に戻る
   */
  const cancelHandler = () => {
    if (isEdited) {
      setShowConfirm(true)
    } else {
      location.href = returnUrl
    }
  }

  const confirmApproveHandler = () => {
    location.href = returnUrl
  }

  const confirmCancelHandler = () => {
    setShowConfirm(false)
  }

  /*
   * 保育内容の追加
   */
  const addSupportEventHandler = (eventType: ISupportEventType) => {
    const supportEvent = buildSupportEvent(
      eventType,
      startDate,
      hour.toString().padStart(2, '0'),
      minute.toString().padStart(2, '0'),
      requestedChildIds,
    )
    append(supportEvent, { shouldFocus: false })
    setSupportEventModal(false)
  }

  const openSupportEventHandler = () => {
    setSupportEventModal(true)
  }

  const closeSupportEventHandler = () => {
    setSupportEventModal(false)
  }

  /*
   * 保育内容の削除
   */

  const deleteSupportEventHandler = (index: number) => {
    setDeleteIndex(index)
    setShowDeleteConfirm(true)
  }

  const deleteApproveHandler = () => {
    remove(deleteIndex)
    setDeleteIndex(undefined)
    setShowDeleteConfirm(false)
  }

  const deleteCancelHandler = () => {
    setShowDeleteConfirm(false)
  }

  /*
   * 編集済みかのハンドリング
   */
  React.useEffect(() => {
    if (isInitialized) {
      setIsEdited(true)
    }
  }, [JSON.stringify(supportEvents)])

  return (
    <Presenter
      isInitialized={isInitialized}
      control={control}
      fields={fields}
      errors={errors}
      errorMessages={errorMessages}
      inputSupportEvents={supportEvents}
      minDate={startDate}
      showToast={showToast}
      showErrorToast={showError}
      showConfirm={showConfirm}
      showDeleteConfirm={showDeleteConfirm}
      showSupportEventModal={showSupportEventModal}
      register={register}
      setValue={setValue}
      submitHandler={submitHandler}
      cancelHandler={cancelHandler}
      addSupportEventHandler={addSupportEventHandler}
      openSupportEventHandler={openSupportEventHandler}
      closeSupportEventHandler={closeSupportEventHandler}
      deleteSupportEventHandler={deleteSupportEventHandler}
      confirmApproveHandler={confirmApproveHandler}
      confirmCancelHandler={confirmCancelHandler}
      deleteApproveHandler={deleteApproveHandler}
      deleteCancelHandler={deleteCancelHandler}
      {...props}
    />
  )
}
